import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Loader2, Download, RotateCcw, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { checkImageFile, checkMessage } from "@/lib/imageCheck";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ToolJobModal from "@/components/ToolJobModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function uploadImageToBackend(file) {
    if (!file) return null;
    if (!file.type.startsWith("image/")) {
        toast.error(i18n.t("tools.common.images_only"));
        return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
}

/**
 * Admin → Tools → Image Upscale (fal-ai/seedvr/upscale/image).
 * Minimal flow: drop photo → submit → poll → show before/after.
 */
export default function AdminToolUpscale() {
    const { t } = useTranslation();
    const [state, setState] = useState(null);
    const [sourceUrl, setSourceUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dropActive, setDropActive] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [job, setJob] = useState(null);
    const [history, setHistory] = useState([]);
    const [model, setModel] = useState("seedvr");
    const [openJob, setOpenJob] = useState(null);
    const pollRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/tools/me");
                setState(data);
            } catch (e) { /* swallow */ }
        })();
        refreshHistory();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    const refreshHistory = async () => {
        try {
            const { data } = await api.get("/tools/jobs", { params: { limit: 30 } });
            setHistory((data.jobs || []).filter((j) => j.tool_key === "upscale"));
        } catch (e) { /* swallow */ }
    };

    const retryJob = async (j) => {
        const msg = state?.is_admin ? "Deze foto opnieuw laten upscalen?" : "Deze foto opnieuw laten upscalen? (kost 1 credit)";
        if (!window.confirm(msg)) return;
        try {
            await api.post(`/tools/jobs/${j.id}/retry`);
            toast.success("Opnieuw gestart — je ziet het resultaat zo verschijnen");
            refreshHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opnieuw proberen mislukt");
        }
    };

    const handleFile = async (file) => {
        if (!file) return;
        const chk = await checkImageFile(file);
        if (!chk.ok) {
            toast.error(checkMessage(chk, file.name));
            return;
        }
        setUploading(true);
        try {
            const url = await uploadImageToBackend(file);
            if (url) {
                setSourceUrl(url);
                setJob(null);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.common.upload_failed"));
        }
        setUploading(false);
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDropActive(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) await handleFile(f);
    };

    const submit = async () => {
        if (!sourceUrl) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/tools/upscale/submit", {
                source_url: sourceUrl,
                model,
            });
            startPolling(data.job_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.upscale_tool.failed"));
            setSubmitting(false);
        }
    };

    const startPolling = (jobId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        const tick = async () => {
            try {
                const { data } = await api.get(`/tools/jobs/${jobId}`);
                setJob(data);
                if (data.status === "completed" || data.status === "failed") {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setSubmitting(false);
                    refreshHistory();
                    api.get("/tools/me").then(({ data }) => setState(data)).catch(() => {});
                    if (data.status === "completed") toast.success(t("tools.upscale_tool.done_toast"));
                    else toast.error(t("tools.upscale_tool.error_prefix", { error: data.error || t("tools.upscale_tool.unknown") }));
                }
            } catch (e) { /* keep polling on transient errors */ }
        };
        tick();
        pollRef.current = setInterval(tick, 2500);
    };

    const reset = () => {
        setSourceUrl(null);
        setJob(null);
    };

    const downloadResult = () => {
        if (!job?.result_url) return;
        const a = document.createElement("a");
        a.href = `${BACKEND}${job.result_url}?dl=upscaled_${Date.now()}.jpg`;
        a.download = `upscaled_${Date.now()}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    // Re-use a finished result as the new source so the user can chain
    // another upscale (or any future tool). Closes the modal and scrolls
    // to the top so the workspace is in view.
    const continueEdit = (j) => {
        if (!j?.result_url) return;
        setSourceUrl(j.result_url);
        setJob(null);
        setOpenJob(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.success(t("tools.upscale_tool.result_loaded"));
    };

    const isAdmin = state?.is_admin;
    const balance = state?.credits_balance ?? 0;

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="tool-upscale-page">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
                <div className="flex items-center justify-between gap-6 mb-8">
                    <Link
                        to={state?.is_admin ? "/admin/tools" : "/tools"}
                        className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950"
                        data-testid="tool-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" /> Tools
                    </Link>
                    <div className="text-xs uppercase tracking-wider text-zinc-500">
                        {isAdmin ? (
                            <span className="text-[#7C3AED]">{t("tools.common.admin_free")}</span>
                        ) : (
                            <span>{t("tools.common.balance_label")} <span className="text-zinc-950 tabular-nums">{balance}</span> {t("tools.common.credits")}</span>
                        )}
                    </div>
                </div>

                <div className="mb-8">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#7C3AED] mb-3 inline-flex items-center gap-2">
                        <Maximize2 className="h-3.5 w-3.5" /> {t("tools.common.ai_tool")}
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        {t("tools.upscale_tool.title")}
                    </h1>
                    <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
                        {t("tools.upscale_tool.subtitle")}
                    </p>
                </div>

                <div
                    className="relative bg-white border border-zinc-200 rounded-sm overflow-hidden min-h-[480px] flex flex-col items-stretch justify-start mb-6"
                    onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
                    onDragLeave={() => setDropActive(false)}
                    onDrop={onDrop}
                    data-testid="upscale-workspace"
                >
                    {!sourceUrl && (
                        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-16">
                            <Upload className="h-8 w-8 text-zinc-900 mb-3" />
                            <p className="text-sm font-medium text-zinc-950 mb-1">{t("tools.common.drop_photo")}</p>
                            <p className="text-xs text-zinc-500 mb-4">{t("tools.common.file_types")}</p>
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="upscale-pick-file-btn"
                                className="bg-white text-zinc-950 hover:bg-zinc-100 rounded-sm h-10 font-medium px-6"
                            >
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                {t("tools.common.pick_file", { defaultValue: "Bestand kiezen" })}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                        </div>
                    )}

                    {sourceUrl && !job?.result_url && (
                        <div className="relative w-full p-3 flex items-center justify-center">
                            <img
                                src={`${BACKEND}${sourceUrl}`}
                                alt="bron"
                                className="max-h-[640px] max-w-full object-contain"
                                data-testid="upscale-source-image"
                            />
                            <div className="absolute top-3 right-3 flex gap-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={reset}
                                    data-testid="upscale-reset-btn"
                                    className="rounded-sm h-8 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                                >
                                    {t("tools.common.remove")}
                                </Button>
                            </div>
                        </div>
                    )}

                    {job?.result_url && (
                        <div className="relative w-full p-3">
                            <BeforeAfterSlider
                                beforeUrl={`${BACKEND}${sourceUrl}`}
                                afterUrl={`${BACKEND}${job.result_url}`}
                            />
                            <div className="absolute top-3 right-3 flex gap-2">
                                <Button
                                    size="sm"
                                    onClick={downloadResult}
                                    data-testid="upscale-download-btn"
                                    className="bg-white text-zinc-950 hover:bg-zinc-100 rounded-sm h-8"
                                >
                                    <Download className="h-3.5 w-3.5 mr-1.5" /> {t("tools.common.download")}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={reset}
                                    data-testid="upscale-new-btn"
                                    className="rounded-sm h-8 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                                >
                                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> {t("tools.upscale_tool.new")}
                                </Button>
                            </div>
                        </div>
                    )}

                    {submitting && (
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-[#7C3AED] mb-3" />
                            <p className="text-sm text-white">{t("tools.upscale_tool.upscaling")}</p>
                            <p className="text-xs text-zinc-500 mt-1">{t("tools.upscale_tool.takes")}</p>
                        </div>
                    )}

                    {dropActive && (
                        <div className="absolute inset-0 border-2 border-dashed border-[#7C3AED] bg-black/40 backdrop-blur-sm flex items-center justify-center pointer-events-none">
                            <p className="text-white text-base">{t("tools.common.drop_to_upload")}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        {(state?.upscale_models || []).map((m) => (
                            <button
                                key={m.key}
                                type="button"
                                onClick={() => setModel(m.key)}
                                data-testid={`upscale-model-${m.key}`}
                                className={`text-sm h-9 px-3 rounded-sm border ${
                                    model === m.key
                                        ? "bg-zinc-100 text-zinc-950 border-zinc-300"
                                        : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                                }`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                    <Button
                        onClick={submit}
                        disabled={!sourceUrl || submitting}
                        data-testid="upscale-submit-btn"
                        className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white font-medium rounded-sm h-11 px-8"
                    >
                        {submitting ? (
                            <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("tools.common.busy")}</>
                        ) : (
                            <><Maximize2 className="h-4 w-4 mr-2" /> {t("tools.upscale_tool.btn")} ({isAdmin ? t("tools.common.free") : t("tools.common.credit_1")})</>
                        )}
                    </Button>
                </div>

                {history.length > 0 && (
                    <div className="mt-12">
                        <div className="flex items-baseline justify-between mb-4">
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{t("tools.upscale_tool.recent")}</p>
                            <p className="text-[11px] text-zinc-500">
                                {t("tools.common.retention")}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                            {history.map((j) => (
                                <button
                                    type="button"
                                    key={j.id}
                                    onClick={() => {
                                        if (j.status === "failed") retryJob(j);
                                        else if (j.result_url) setOpenJob(j);
                                    }}
                                    disabled={!j.result_url && j.status !== "failed"}
                                    className="relative aspect-square bg-white border border-zinc-200 rounded-sm overflow-hidden group hover:border-zinc-400 transition-colors text-left disabled:cursor-default"
                                    data-testid={`upscale-history-${j.id}`}
                                >
                                    {j.result_url ? (
                                        <>
                                            <img
                                                src={`${BACKEND}${j.result_url}`}
                                                alt=""
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                            <a
                                                href={`${BACKEND}${j.result_url}?dl=upscaled_${j.id.slice(0, 8)}.jpg`}
                                                download={`upscaled_${j.id.slice(0, 8)}.jpg`}
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`upscale-history-download-${j.id}`}
                                                title="Download"
                                                className="absolute top-1.5 right-1.5 h-7 w-7 flex items-center justify-center rounded-sm bg-black/70 hover:bg-black text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
                                            >
                                                <Download className="h-3.5 w-3.5" />
                                            </a>
                                        </>
                                    ) : j.status === "failed" ? (
                                        <div
                                            className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 bg-red-50/60 text-center"
                                            title={j.error || ""}
                                            data-testid={`history-failed-${j.id}`}
                                        >
                                            <span className="text-[11px] font-bold text-red-600">Mislukt</span>
                                            {j.error && (
                                                <span className="text-[10px] leading-snug text-red-500/90 line-clamp-3 break-words">
                                                    {(j.error.match(/'msg':\s*["']([^"']+)["']/) || [])[1] || j.error}
                                                </span>
                                            )}
                                            <span className="text-[10px] font-bold text-zinc-600 underline underline-offset-2">↻ Opnieuw proberen</span>
                                        </div>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-[11px] text-zinc-500">
                                            {j.status}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <ToolJobModal
                job={openJob}
                onClose={() => setOpenJob(null)}
                onContinueEdit={continueEdit}
            />
        </div>
    );
}
